import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useFullscreen } from 'react-use';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import { TourProvider } from '@reactour/tour';
import ThemeContext from '../contexts/themeContext';

import Aside from '../layout/Aside/Aside';
import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import { authPages, othersMenu, publicPages } from '../menu';
import { Toast, ToastContainer } from '../components/bootstrap/Toasts';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS } from '../helpers/helpers';
import steps, { styles } from '../steps';
import { DefaultContext } from '../contexts/default';

import '../styles.css'

const App = () => {
  getOS();

  const { user } = useContext(DefaultContext)

  const navigate = useNavigate();

  useEffect(() => {
    function ignoreOthersPath() {
      const path = window.location.pathname.replaceAll('/', '');
      if (path === '')
        return false
      const publicRoutes = { ...othersMenu, ...publicPages };
      return Object.keys(publicRoutes).find(key => publicRoutes[key].path.includes(path))
    }
    function redirectPage() {
      if (user) {
        navigate("/dashboard");
      } else {
        navigate("/");
      }
    }

    if (!ignoreOthersPath())
      redirectPage();
  }, [user]);

  /**
   * Dark Mode
   */
  const { themeStatus, darkModeStatus } = useDarkMode();
  const theme = {
    theme: themeStatus,
    primary: COLORS.PRIMARY.code,
    secondary: COLORS.SECONDARY.code,
    success: COLORS.SUCCESS.code,
    info: COLORS.INFO.code,
    warning: COLORS.WARNING.code,
    danger: COLORS.DANGER.code,
    dark: COLORS.DARK.code,
    light: COLORS.LIGHT.code,
  };
  useEffect(() => {
    if (darkModeStatus) {
      document.documentElement.setAttribute('theme', 'dark');
    }
    return () => {
      document.documentElement.removeAttribute('theme');
    };
  }, [darkModeStatus]);

  /**
   * Full Screen
   */
  const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
  const ref = useRef(null);
  useFullscreen(ref, fullScreenStatus, {
    onClose: () => setFullScreenStatus(false),
  });

  /**
   * Modern Design
   */
  useLayoutEffect(() => {
    if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
      document.body.classList.add('modern-design');
    } else {
      document.body.classList.remove('modern-design');
    }
  });

  //	Add paths to the array that you don't want to be "Aside".
  const withOutAsidePages = [
    othersMenu.monitor_login.path,
    authPages.login.path,
    authPages.signUp.path,
    authPages.page404.path,
    othersMenu.termsOfUse.path,
    othersMenu.orderMonitor.path,
    publicPages.evaluation.path,
    publicPages.clientRegister.path,
    othersMenu.resetPassword.path,
    
  ];

  return (
    <ThemeProvider theme={theme}>
      <ToastProvider components={{ ToastContainer, Toast }}>
        <TourProvider
          steps={steps}
          styles={styles}
          showNavigation={false}
          showBadge={false}>
          <div
            ref={ref}
            className='app'
            style={{
              backgroundColor: fullScreenStatus && 'var(--bs-body-bg)',
              zIndex: fullScreenStatus && 1,
              overflow: fullScreenStatus && 'scroll',
            }}>
            <Routes>
              {withOutAsidePages.map((path) => (
                <Route key={path} path={path} />
              ))}
              <Route path='*' element={<Aside />} />

              <Route
                path="*"
                element={<Navigate to="/dashboard" />}
              />
            </Routes>
            <Wrapper />
          </div>
          <Portal id='portal-notification'>
            <ReactNotifications />
          </Portal>
        </TourProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};

export default App;
